import { useCallback } from 'react';
import { noop } from 'lodash';
import Link from 'next/link';
import styled from 'styled-components';
import { select } from '@frameio/i18n';
import { getDevFlag } from '@frameio/shared/dev-flags';
import type { ContextMenuEvent } from '@frameio/shared/hooks';
import { TransferBatchStatus } from '@frameio/shared/transfers/states';
import { IconButton, Menu, MenuButton, MenuGroup, theme } from '@frameio/vapor';
import {
  IconArrowClockwise,
  IconArrowUpRight,
  IconMoreHorizontal,
  IconOverview,
  IconPauseCircleFill,
  IconPlay,
  IconTrash,
  IconX,
} from '@frameio/vapor-icons';
import type { TransferBatch } from '../../types/uploader';
import {
  getTransferBatchHasLocalData,
  getTransferBatchType,
} from '../../utils';
import TEXT from './generated/TEXT/JobControls';

TEXT.DEFINE(
  {
    /** Menu button label to cancel a transfer */
    cancel: 'Cancel',
    /** Menu button label to remove a completed transfer from the list */
    removeFromList: 'Remove From List',
    /** Menu button label to navigate to the project */
    showInProject: 'Show in Project',
    /** Menu button label to view the log file */
    viewLogFile: 'View Log File',
    /** Menu button label to pause a transfer */
    pause: 'Pause',
    /** Menu button label to resume a paused transfer */
    resume: 'Resume',
    /** Menu button label to retry a failed transfer */
    retry: 'Retry',
    /** Dev-only menu button to force a transfer failure */
    forceFailure: 'Force Failure',
    /** Label for transfer type based on batch type */
    progressTypeLabel: (batchType: string): string =>
      select(batchType, {
        DOWNLOAD: 'Download',
        RENDER: 'Export',
        UPLOAD: 'Upload',
        other: '',
      }),
  },
  { select }
);

type PopperJsVirtualElementCompatibleType = {
  getBoundingClientRect: () => DOMRect;
};

type JobControlsProps = {
  job: TransferBatch;
  className?: string;
  projectPath: string | URL;
  onCancelClick?: (id: string) => void;
  onForceFailureClick?: (id: string) => void;
  onRemoveClick?: (id: string, jobId: string) => void;
  onOpenFilePath?: () => void;
  setIsContextMenuOpen?: (isOpen: boolean) => void;
  onResumeClick?: (id: string) => void;
  onRetryClick?: (id: string) => void;
  onPauseClick?: (id: string) => void;
  showViewLogFile: boolean;
  iconSize?: 16 | 24;
  anchorPosition: PopperJsVirtualElementCompatibleType;
  isContextMenuOpen: boolean;
  handleContextMenuEvent: (e: ContextMenuEvent) => void;
};

const isSegmentDebuggerEnabled = getDevFlag('SEGMENT_DEBUGGER');

function Component({
  anchorPosition,
  job,
  isContextMenuOpen,
  projectPath,
  className,
  iconSize = 24,
  onCancelClick = noop,
  onForceFailureClick = noop,
  onRemoveClick = noop,
  onPauseClick = noop,
  onResumeClick = noop,
  onRetryClick,
  onOpenFilePath = noop,
  setIsContextMenuOpen = noop,
  showViewLogFile = false,
  handleContextMenuEvent,
}: JobControlsProps) {
  const { id, localUploadingId, status, batchType, isPaused, fileCounts } = job;
  const hasLocalData = getTransferBatchHasLocalData(job);
  const jobType = getTransferBatchType(job);
  const isJobCanceling = status === TransferBatchStatus.CANCELING;
  const isUploading = status === TransferBatchStatus.UPLOADING;
  const isDownloading = status === TransferBatchStatus.DOWNLOADING;
  const isQueued = status === TransferBatchStatus.IDLE;
  const isProcessing = status === TransferBatchStatus.PROCESSING;

  const isPausedDisabled = hasLocalData
    ? !isUploading && !isDownloading && !isPaused
    : true;
  const canCancel = hasLocalData
    ? (isQueued || isUploading || isDownloading || isProcessing) &&
      !isJobCanceling
    : false;

  const canPause = hasLocalData
    ? (isUploading || isDownloading) && !isJobCanceling
    : false;

  const canRemoveFromList =
    (!isQueued && !isUploading && !isDownloading && !isProcessing) ||
    !hasLocalData;
  const hasBatchFailures =
    jobType === 'BATCH' && fileCounts?.failed && fileCounts.failed > 0;
  const canRetry = status === TransferBatchStatus.FAILED || hasBatchFailures;

  const handleOnCancelClick = useCallback(() => {
    onCancelClick(localUploadingId);
  }, [onCancelClick, localUploadingId]);

  const handleOnMouseDown = useCallback(
    (e: ContextMenuEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsContextMenuOpen(false);
    },
    [setIsContextMenuOpen]
  );

  const handleOnForceFailureClick = useCallback(() => {
    onForceFailureClick(localUploadingId);
  }, [onForceFailureClick, localUploadingId]);

  return (
    <div className={`controls ${className}`}>
      <Menu
        anchorPosition={anchorPosition}
        content={
          <>
            <MenuGroup>
              {batchType === 'UPLOAD' && onRetryClick && (
                <MenuButton
                  data-testid="job-card--retry-button"
                  disabled={!canRetry}
                  iconBefore={<IconArrowClockwise />}
                  onSelect={() => onRetryClick(id)}
                >
                  {TEXT.retry} {TEXT.progressTypeLabel(batchType)}
                </MenuButton>
              )}

              {canPause ? (
                <MenuButton
                  data-testid="job-card--pause-button"
                  disabled={isPausedDisabled}
                  iconBefore={
                    !isPaused ? (
                      <IconPauseCircleFill data-testid="job-card--is-active" />
                    ) : (
                      <IconPlay data-testid="job-card--is-paused" />
                    )
                  }
                  onSelect={() =>
                    !isPaused
                      ? onPauseClick(localUploadingId)
                      : onResumeClick(localUploadingId)
                  }
                >
                  {!isPaused ? TEXT.pause : TEXT.resume}{' '}
                  {TEXT.progressTypeLabel(batchType)}
                </MenuButton>
              ) : null}

              <Link href={projectPath}>
                <MenuButton
                  iconBefore={<IconArrowUpRight />}
                  onSelect={() => {
                    setIsContextMenuOpen(false);
                  }}
                >
                  {TEXT.showInProject}
                </MenuButton>
              </Link>
              {showViewLogFile && (
                <MenuButton
                  disabled={isQueued}
                  iconBefore={<IconOverview />}
                  onSelect={onOpenFilePath}
                >
                  {TEXT.viewLogFile}
                </MenuButton>
              )}
              {canRemoveFromList && (
                <MenuButton
                  data-testid="job-card--remove-button"
                  disabled={!canRemoveFromList}
                  iconBefore={<IconTrash />}
                  onSelect={() => onRemoveClick(id, localUploadingId)}
                >
                  {TEXT.removeFromList}
                </MenuButton>
              )}
            </MenuGroup>
            {canCancel && (
              <MenuGroup>
                <MenuButton
                  data-testid="job-card--cancel-button"
                  disabled={!canCancel}
                  iconBefore={<IconX />}
                  onSelect={handleOnCancelClick}
                >
                  {TEXT.cancel} {TEXT.progressTypeLabel(batchType)}
                </MenuButton>
                {isSegmentDebuggerEnabled && (
                  <MenuButton
                    data-testid="job-card--force-failure-button"
                    disabled={!canCancel}
                    iconBefore={<IconX />}
                    onSelect={handleOnForceFailureClick}
                  >
                    {TEXT.forceFailure}
                  </MenuButton>
                )}
              </MenuGroup>
            )}
          </>
        }
        isShowing={isContextMenuOpen}
        onRequestChange={setIsContextMenuOpen}
        placement={'bottom-start'}
      >
        <div>
          <StyledIconButton
            data-testid="job-card-controls-button"
            icon={<IconMoreHorizontal size={iconSize} />}
            label="Context menu"
            onBlur={() => setIsContextMenuOpen(false)}
            onClick={handleContextMenuEvent}
            onMouseDown={handleOnMouseDown}
            size="small"
            variant="link"
          />
        </div>
      </Menu>
    </div>
  );
}

/**
 * Icon buttons to do stuff like pause/resume/cancel the job.
 */
export const JobControls = styled(Component)`
  align-items: center;
  display: flex;
  [role='separator'] {
    height: 12px;
  }
`;

/** @todo (VAPR-1075): Remove specific styling once Button variants synced with design. */
const StyledIconButton = styled(IconButton)`
  color: ${theme.colors.icon.primary};
`;
